import React, { useCallback, useEffect, useState } from 'react';
import block from 'propmods';
import { Skill, Channel } from '../../model/skill';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Monitoring';
import { getMonitoringData } from '../../api';
import { useLoadingData } from '../../hooks/useLoadingData';
import { usePushHistory } from '../../hooks/usePushHistory';
import { Loading } from '../Loading/Loading';
import { Header } from '../Form/Form';
import { MonitoringPageHistoryControls } from '../MonitoringPageHistoryControls/MonitoringPageHistoryControls';
import { getDayRange } from '../../utils/monitoring';
import { Datepicker } from '../Datepicker/Datepicker';
import { moment } from '../../lib/moment';
import { MonitoringPageSelect } from '../MonitoringPageSelect/MonitoringPageSelect';
import { MonitoringPageDashboardSection } from '../MonitoringPageDashboardSection/MonitoringPageDashboardSection';
import { SolomonLabels } from '../../model/solomon';
import { textToKey } from '../../lib/i18n-utils';
import './MonitoringPageDashboard.scss';

const t = i18n(keyset);
const b = block('MonitoringPageDashboard');

type I18NLiteral = keyof (typeof keyset)[keyof typeof keyset];

interface Props {
    skill: Skill;
    solomonLabels: SolomonLabels;
}

export const MonitoringPageDashboard: React.FC<Props> = ({ skill, solomonLabels }) => {
    const channel = skill.channel as Channel.AliceSkill | Channel.SmartHome;
    const commands = solomonLabels.commands[channel];
    const sources = solomonLabels.sources[channel];

    const [source, setSource] = useState(sources[0].value);
    const [command, setCommand] = useState(commands[0].value);
    const {
        currentValue: currentRange,
        goBack: goPrevRange,
        goForward: goNextRange,
        init: initRanges,
        push: pushRange,
        hasNext: hasNextRange,
        hasPrev: hasPrevRange,
        values: timeRanges,
    } = usePushHistory([getDayRange()]);
    const { from, to } = currentRange;

    const loader = useCallback(() => {
        return getMonitoringData(skill.id, {
            command,
            source,
            from,
            to,
        });
    }, [skill.id, command, source, from, to]);

    const { loadingMeta, reload } = useLoadingData({ loader });

    const { state, data: chartRowsMeta } = loadingMeta;

    useEffect(reload, [command, source, from, to]);

    const onIntervalChange = (startTs: number, endTs: number) => {
        const newFrom = Math.min(endTs, startTs);
        const newTo = Math.max(endTs, startTs);

        if (newTo - newFrom < __appConfig.monitoring.minChartInterval) {
            return;
        }

        pushRange({ from: newFrom, to: newTo });
    };

    const displayHistoryControls = hasNextRange || hasPrevRange;
    const isSettingsDisabled = state === 'loading';

    if (!chartRowsMeta) {
        return <Loading centered />;
    }
    return (
        <div {...b()}>
            <div {...b('settings')}>
                <div {...b('datepicker')}>
                    <Datepicker
                        disabled={isSettingsDisabled}
                        from={moment(from)}
                        to={moment(to)}
                        onChange={(from, to) => {
                            const fromTs = from.valueOf();
                            const toTs = to.valueOf();

                            if (toTs - fromTs < __appConfig.monitoring.minChartInterval) {
                                return;
                            }

                            initRanges([{ from: fromTs, to: toTs }]);
                        }}
                    />
                </div>
                <div {...b('options')}>
                    <div {...b('option')}>
                        <div {...b('option-label')}>{t('option-source')}</div>
                        <MonitoringPageSelect
                            namespace={'source'}
                            options={sources}
                            value={source}
                            onChange={setSource}
                            disabled={isSettingsDisabled}
                        />
                    </div>
                    <div {...b('option')}>
                        <div {...b('option-label')}>{t('option-request-type')}</div>
                        <MonitoringPageSelect
                            namespace="request_type"
                            options={commands}
                            value={command}
                            onChange={setCommand}
                            disabled={isSettingsDisabled}
                        />
                    </div>
                </div>
            </div>
            <div {...b('sections')}>
                {chartRowsMeta!.map(({ text, charts }, i) => {
                    return (
                        <div key={i}>
                            <div {...b('row-header')}>
                                <Header>{t(textToKey(text) as I18NLiteral)}</Header>
                                {displayHistoryControls && (
                                    <MonitoringPageHistoryControls
                                        goNextRange={goNextRange}
                                        goPrevRange={goPrevRange}
                                        hasNextRange={hasNextRange}
                                        hasPrevRange={hasPrevRange}
                                        initRanges={() => initRanges(timeRanges.slice(0, 1))}
                                    />
                                )}
                            </div>
                            <div {...b('section')}>
                                <MonitoringPageDashboardSection
                                    charts={charts}
                                    onIntervalChange={onIntervalChange}
                                    dataLoadingState={state}
                                    from={from}
                                    to={to}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
